import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ContentControlToFieldMapService } from '../content-control-to-field-map.service';
import { ContentControlToSignatureFieldService } from '../esignature/content-control-to-signature-field.service';
import { ContentControlId } from './content-control-id';
import { DocumentSelectionChangedService } from './document-selection-changed-service';

/**
 * Fires an event when a ContentControl has been deleted from the document via a user selection change (e.g. delete keypress).
 *
 * Fires an event when the set of selected content controls changes.
 */
@Injectable({ providedIn: 'root' })
export class ContentControlMonitor {
  private contentControlsDeletedSubject = new Subject<ContentControlId>();
  contentControlsDeleted$ = this.contentControlsDeletedSubject.asObservable();
  private contentControlsAddedSubject = new Subject<ContentControlId[]>();
  contentControlsAdded$ = this.contentControlsAddedSubject.asObservable();
  private contentControlSelectionChangedSubject = new Subject<ContentControlId[]>();
  contentControlSelectionChanged$ = this.contentControlSelectionChangedSubject.asObservable();
  private oldContentControlList: ContentControlId[] = [];
  private oldSelectedContentControlList: ContentControlId[] | undefined = undefined;

  constructor(
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private contentControlToSignatureFieldService: ContentControlToSignatureFieldService,
    documentSelectionChangedService: DocumentSelectionChangedService
  ) {
    (async () => {
      this.oldContentControlList = await this.getAllContentControlsInDocument();
    })();

    documentSelectionChangedService.selectionChanged$.subscribe(this.selectionChanged.bind(this));
  }

  // TODO: move this to the WordService maybe?
  /**
   * Gets the Content Controls that belong to the add-in that are currently selected or empty list if none selected.
   */
  async getSelectedContentControls(): Promise<ContentControlId[]> {
    return Word.run(async (ctx) => {
      try {
        // TODO: make this a function in the word.service
        const currSel = ctx.document.getSelection();
        currSel.load();
        currSel.contentControls.load();
        await ctx.sync();

        return this.filterAndMapWordContentControlsToFieldControls(currSel.contentControls.items);
      } catch (err) {
        console.error('Error trying to determine ContentControl selection.', err);
        return [];
      }
    });
  }

  private selectionChanged(_eventArgs: Office.DocumentSelectionChangedEventArgs): void {
    (async () => {
      await this.determineDifference();
      await this.determineSelection();
    })();
  }

  private async determineDifference(): Promise<void> {
    const currentContentControlList = await this.getAllContentControlsInDocument();
    const added = this.complement(this.oldContentControlList, currentContentControlList);
    const deleted = this.complement(currentContentControlList, this.oldContentControlList);
    this.oldContentControlList = currentContentControlList;

    if (added.length > 0) {
      this.contentControlsAddedSubject.next(added);
    }
    // added.forEach(ccId => this.contentControlsAddedSubject.next({...ccId, type: ContentControlEventType.Added}));

    deleted.forEach((ccId) => this.contentControlsDeletedSubject.next({ ...ccId }));
  }

  private async determineSelection(): Promise<void> {
    const selection = await this.getSelectedContentControls();
    if (!this.same(selection, this.oldSelectedContentControlList)) {
      this.oldSelectedContentControlList = selection;
      this.contentControlSelectionChangedSubject.next(selection);
    }
  }

  private async getAllContentControlsInDocument(): Promise<ContentControlId[]> {
    return Word.run(async (ctx) => {
      ctx.document.contentControls.load('items'); // TODO: change to use the word.service
      await ctx.sync();
      return ctx.document.contentControls.items.map((wcc) => this.contentControlMapper(wcc));
    });
  }

  private filterAndMapWordContentControlsToFieldControls(contentControls: Word.ContentControl[]): ContentControlId[] {
    const fieldControls = this.contentControlToFieldMapService.filter(contentControls);
    const signatureControls = this.contentControlToSignatureFieldService.filter(contentControls);
    const dataControls = [...fieldControls, ...signatureControls];
    return dataControls.map((wcc) => this.contentControlMapper(wcc));
  }

  private contentControlMapper(wcc: Word.ContentControl): ContentControlId {
    return { id: wcc.id, tag: wcc.tag, title: wcc.title };
  }

  /**
   * Gets the elements that are not in set A (i.e. the complement).
   * @param a elements of set A
   * @param b elements of set B
   * @return the elements NOT in set A
   */
  private complement(a: ContentControlId[], b: ContentControlId[]): ContentControlId[] {
    const elementsNotInA: ContentControlId[] = [];
    b.forEach((bCCId) => {
      const inA = a.find((aCCId) => aCCId.id === bCCId.id);
      if (!inA) {
        elementsNotInA.push(bCCId);
      }
    });
    return elementsNotInA;
  }

  /**
   * Checks if the two lists are the same based on the unique content control id
   * @param a elements of set A
   * @param b elements of set B
   * @return true if the two sets contain the same controls; false otherwise
   */
  private same(a: ContentControlId[] | undefined, b: ContentControlId[] | undefined): boolean {
    return <boolean>(a?.length === b?.length && a?.every((av) => b?.find((bv) => bv.id === av.id)));
  }
}
